import { formatRelative } from "@brandex/i18n";
import { useBrandex, websiteUrl } from "@brandex/dashboard";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Spinner } from "@brandex/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, PenSquare, RefreshCw } from "lucide-react";
import { useState } from "react";

import { useWebsites, websiteKeys, type AdminWebsite } from "../data";
import { QueryError } from "../views";
import { can, useStaffAccess } from "../access";

const STATE: Record<AdminWebsite["website"]["state"], { label: string; tone: "success" | "warning" | "neutral" | "accent" }> = {
  live: { label: "Live", tone: "success" },
  awaiting_activation: { label: "Awaiting activation", tone: "warning" },
  awaiting_verification: { label: "Awaiting BMDC check", tone: "warning" },
  offline: { label: "Offline", tone: "warning" },
  unpublished: { label: "Unpublished by doctor", tone: "warning" },
  not_included: { label: "Not in package", tone: "neutral" },
  needs_onboarding: { label: "Needs onboarding", tone: "neutral" },
};

type CustomDomain = NonNullable<AdminWebsite["website"]["custom_domain"]>;

/** The public web origin doctor sites live under (`/d/{slug}`). */
const SITE_URL: string = (import.meta.env.VITE_WEB_BASE_URL as string | undefined) ?? "http://localhost:3000";

/**
 * Publish and domains (F-21, ADR 0013): every onboarded doctor has a site on
 * a platform subdomain (`{name}.{sites domain}`), public while their package
 * includes the website and is running. Staff buy and connect the doctor's own
 * domain; it serves only after DNS is verified.
 */
export function WebsitesPage() {
  const sites = useWebsites();
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Publish and domains</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every doctor site gets a BrandEx subdomain at onboarding. Connect a domain you bought for the doctor, then verify its DNS.
        </p>
      </header>
      <DnsHelp />
      {sites.isPending ? <Spinner label="Loading…" /> : null}
      {sites.isError ? <QueryError error={sites.error} /> : null}
      {sites.data !== undefined ? (
        <div className="glass overflow-x-auto rounded-[var(--radius-lg)]">
          <table className="w-full min-w-[1040px] text-sm">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">Doctor</th><th>Subdomain</th><th>Package</th><th>Custom domain</th><th className="px-4">Site</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sites.data.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No doctor has finished onboarding yet.</td></tr>
              ) : (
                sites.data.map((s) => <WebsiteRow key={s.organization_id} s={s} />)
              )}
            </tbody>
          </table>
          {sites.hasNextPage ? (
            <div className="p-3"><Button size="sm" variant="outline" loading={sites.isFetchingNextPage} onClick={() => void sites.fetchNextPage()}>Load more</Button></div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function WebsiteRow({ s }: { s: AdminWebsite }) {
  const { api, platform, nav } = useBrandex();
  // Editing what the site shows: super admins, Operations, and roles with Edit client content.
  const canEdit = can(useStaffAccess().data, "platform.clients.manage", { assigned: true });
  const qc = useQueryClient();
  const status = STATE[s.website.state];
  const refresh = () => void qc.invalidateQueries({ queryKey: websiteKeys.list });
  const path = { params: { path: { org_id: s.organization_id } } };
  const setStatus = useMutation({
    mutationFn: async (next: "online" | "offline") => {
      await api.http.PUT("/v1/admin/websites/{org_id}/status", { ...path, body: { status: next } });
    },
    onSuccess: refresh,
  });
  const url = websiteUrl(s.website, SITE_URL);
  const name = s.doctor_name ?? s.organization_id;
  return (
    <tr className="align-top">
      <td className="px-4 py-3">
        <p className="font-medium">{s.doctor_name ?? "—"}</p>
        <p className="text-xs text-muted-foreground">{s.organization_id}</p>
      </td>
      <td className="py-3 pr-3"><SubdomainCell s={s} name={name} onSaved={refresh} /></td>
      <td className="py-3">
        <p>{s.subscription_status ?? "none"}</p>
        <p className="text-xs text-muted-foreground">{s.includes_website ? "includes website" : "no website add-on"}</p>
      </td>
      <td className="py-3 pr-3"><DomainCell s={s} name={name} onSaved={refresh} /></td>
      <td className="px-4 py-3">
        <Badge tone={status.tone}>{status.label}</Badge>
        <div className="mt-2 flex flex-wrap gap-1">
          {canEdit && s.website.state !== "needs_onboarding" ? (
            <Button size="sm" variant="outline" aria-label={`Edit content: ${name}`} onClick={() => nav.navigate(`/clients/${s.organization_id}/manage?tab=profile`)}>
              <PenSquare className="h-3 w-3" aria-hidden /> Edit content
            </Button>
          ) : null}
          {s.website.state === "live" ? (
            <Button size="sm" variant="ghost" onClick={() => void platform.openExternal(url)}>
              Open <ExternalLink className="h-3 w-3" aria-hidden />
            </Button>
          ) : null}
          {s.website.status === "online" ? (
            <Button size="sm" variant="ghost" loading={setStatus.isPending} onClick={() => setStatus.mutate("offline")}>Take offline</Button>
          ) : (
            <Button size="sm" variant="ghost" loading={setStatus.isPending} onClick={() => setStatus.mutate("online")}>Bring online</Button>
          )}
        </div>
        {setStatus.isError ? <div className="mt-2"><QueryError error={setStatus.error} /></div> : null}
      </td>
    </tr>
  );
}

function SubdomainCell({ s, name, onSaved }: { s: AdminWebsite; name: string; onSaved: () => void }) {
  const { api } = useBrandex();
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (subdomain: string) => {
      await api.http.PUT("/v1/admin/websites/{org_id}/subdomain", { params: { path: { org_id: s.organization_id } }, body: { subdomain } });
    },
    onSuccess: () => {
      setDraft(null);
      onSaved();
    },
  });
  const host = hostOf(s.website.subdomain_url);
  if (draft === null) {
    return (
      <div>
        <code className="text-xs">{host}</code>
        <Button size="sm" variant="ghost" className="ml-1" onClick={() => setDraft(s.website.subdomain)}>Rename</Button>
      </div>
    );
  }
  return (
    <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(draft.trim()); }}>
      <div className="flex items-center gap-1">
        <Input aria-label={`Subdomain for ${name}`} className="h-8 w-40" value={draft} onChange={(e) => setDraft(e.target.value.toLowerCase())} autoFocus />
        <span className="text-xs text-muted-foreground">.{host.split(".").slice(1).join(".")}</span>
      </div>
      <div className="flex gap-1">
        <Button size="sm" type="submit" loading={save.isPending}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
      </div>
      {save.isError ? <QueryError error={save.error} /> : null}
    </form>
  );
}

interface DomainDraft {
  domain: string;
  registrar: string;
  renewsOn: string;
}

function DomainCell({ s, name, onSaved }: { s: AdminWebsite; name: string; onSaved: () => void }) {
  const { api, locale } = useBrandex();
  const [draft, setDraft] = useState<DomainDraft | null>(null);
  const current: CustomDomain | null = s.website.custom_domain ?? null;
  const path = { params: { path: { org_id: s.organization_id } } };
  const done = () => {
    setDraft(null);
    onSaved();
  };
  const save = useMutation({
    mutationFn: async (d: DomainDraft | null) => {
      await api.http.PUT("/v1/admin/websites/{org_id}/domain", {
        ...path,
        body: d === null || d.domain.trim() === ""
          ? { domain: null }
          : { domain: d.domain.trim(), registrar: d.registrar.trim() || null, renews_on: d.renewsOn || null },
      });
    },
    onSuccess: done,
  });
  const check = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/websites/{org_id}/domain/check", path);
    },
    onSuccess: onSaved,
  });
  const verify = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/websites/{org_id}/domain/verify", path);
    },
    onSuccess: onSaved,
  });

  if (draft !== null) {
    return (
      <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate(draft); }}>
        <Input aria-label={`Custom domain for ${name}`} className="h-8 w-56" placeholder="drexample.com" value={draft.domain} onChange={(e) => setDraft({ ...draft, domain: e.target.value })} autoFocus />
        <div className="flex gap-2">
          <Input aria-label={`Registrar for ${name}`} className="h-8 w-28" placeholder="Registrar" value={draft.registrar} onChange={(e) => setDraft({ ...draft, registrar: e.target.value })} />
          <Input aria-label={`Renews on for ${name}`} className="h-8 w-36" type="date" value={draft.renewsOn} onChange={(e) => setDraft({ ...draft, renewsOn: e.target.value })} />
        </div>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" type="submit" loading={save.isPending}>Save</Button>
          {current !== null ? <Button size="sm" variant="ghost" onClick={() => save.mutate(null)}>Disconnect</Button> : null}
          <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
        </div>
        {save.isError ? <QueryError error={save.error} /> : null}
      </form>
    );
  }

  const edit = () => setDraft({ domain: current?.name ?? "", registrar: current?.registrar ?? "", renewsOn: current?.renews_on ?? "" });
  if (current === null) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Not set</span>
        <Button size="sm" variant="ghost" onClick={edit}>Connect</Button>
      </div>
    );
  }
  const active = current.status === "active";
  return (
    <div className="grid gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{current.name}</span>
        <Badge tone={active ? "success" : "warning"}>{active ? "Verified" : "Waiting for DNS"}</Badge>
      </div>
      {current.registrar !== null && current.registrar !== undefined ? (
        <p className="text-xs text-muted-foreground">
          {current.registrar}{current.renews_on ? ` · renews ${current.renews_on}` : ""}
        </p>
      ) : current.renews_on ? <p className="text-xs text-muted-foreground">Renews {current.renews_on}</p> : null}
      {current.check_error ? (
        <p className="text-xs text-destructive">DNS {current.check_error}{current.checked_at ? ` (${formatRelative(locale, current.checked_at)})` : ""}</p>
      ) : null}
      <div className="flex flex-wrap gap-1">
        <Button size="sm" variant="ghost" loading={check.isPending} onClick={() => check.mutate()}>
          <RefreshCw className="h-3 w-3" aria-hidden /> Check DNS
        </Button>
        {!active ? (
          <Button size="sm" variant="ghost" loading={verify.isPending} onClick={() => verify.mutate()}>
            <CheckCircle2 className="h-3 w-3" aria-hidden /> Mark verified
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={edit}>Change</Button>
      </div>
      {check.isError ? <QueryError error={check.error} /> : null}
      {verify.isError ? <QueryError error={verify.error} /> : null}
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function DnsHelp() {
  const host = hostOf(SITE_URL);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">How doctor sites are published</CardTitle>
        <CardDescription>
          A site is live on its subdomain as soon as the package with the website add-on is active. A doctor's own domain is served after its DNS is verified; HTTPS certificates are issued on first visit.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ol className="list-decimal space-y-1 pl-5 text-sm">
          <li>Buy the domain for the doctor, then enter it here with the registrar and renewal date (so it is renewed on time).</li>
          <li>At the registrar, point the bare domain to the BrandEx ingress with an <code>A</code> record (or <code>ALIAS</code>), and <code>www</code> with a <code>CNAME</code> to <code>{host}</code>.</li>
          <li>Press <strong>Check DNS</strong>. When it points here, the domain becomes <strong>Verified</strong> and starts serving. Behind a CDN that hides the address, check it yourself and use <strong>Mark verified</strong> (audited).</li>
          <li>Open the domain. It shows only that doctor's site, in English at <code>/</code> and Bangla at <code>/bn</code>.</li>
        </ol>
        <Alert tone="info" className="mt-3">Unverified domains get no content and no certificate, so nobody can point a domain at BrandEx and borrow a doctor's site.</Alert>
      </CardContent>
    </Card>
  );
}
